import { logger, type AudioPosition } from '@tunasong/models'
import { useDelayedInvoke } from '@tunasong/ui-lib'
import React, { useCallback, useEffect, useState } from 'react'
import { useAudioEngine } from '../hooks/audio-engine.js'

export interface PlayerProps {
  /** Name of the player to be shown in the Mixer */
  el: HTMLMediaElement | null
  name?: string
  volume?: number
  /** Automatically stop when another player is started */
  autoStop?: boolean
  // Fraction of the speed the audio is to be played, e.g. 0.5 for half the audio
  playbackRate?: number

  onPlay?(): void
  onPause?(): void
}

export interface MixedPlayer {
  audioProps: Partial<React.DetailedHTMLProps<React.AudioHTMLAttributes<HTMLAudioElement>, HTMLAudioElement>>
  playing: boolean
  duration: number
  position: number
  play(): Promise<void>
  pause(): void
  seek(seconds: number): void
  togglePlay(): void
}

/**
 * The AudioPlayer will play a single audio file. Events will he scheduled on the Timeline.
 * Only one AudioPlayer works at the same time because the Timeline is a singleton connected to
 * the AudioContext.
 */

export const useAudioPlayer = ({ el, onPause, onPlay, playbackRate }: PlayerProps): MixedPlayer => {
  const playing = Boolean(el && el.paused !== true)
  const [pending, setPending] = useState(false)
  const engine = useAudioEngine()

  const schedulePlay = () => {
    if (!el) {
      return
    }
    const playWhenReady = () => {
      logger.debug('Scheduled play running...', { el, playing, pending, readyState: el.readyState })
      void play()
    }

    el.addEventListener('canplay', playWhenReady, { once: true })
  }

  // Set the playback rate if provided
  useEffect(() => {
    if (el && playbackRate) {
      logger.debug('Setting playback rate', { playbackRate, el })
      // eslint-disable-next-line react-compiler/react-compiler
      el.playbackRate = playbackRate
    }
  }, [el, playbackRate])

  const play = async () => {
    if (!el || pending) {
      return
    }

    if (el.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      logger.debug('Audio not ready to play - scheduling ', { readyState: el.readyState })
      schedulePlay()
      return
    }

    setPending(true)
    // Ensure the audio engine is started
    if (!engine.started) {
      await engine.start()
    }

    if (onPlay) {
      onPlay()
    }

    // el.pause()

    return el
      .play()
      .catch(err => {
        if (err !== null && typeof err === 'object' && 'name' in err && err.name === 'AbortError') {
          return
        }
        logger.error('Error playing', err)
        throw err
      })
      .finally(() => {
        setPending(false)
      })
  }

  const seek = (seconds: number | AudioPosition) => {
    if (!el) {
      return
    }
    const secs = typeof seconds === 'number' ? seconds : seconds.seconds
    /** We will sync the transport to this when seek is done */

    /**
     * Setting currentTime before HAVE_METADATA (readyState 1) is a no-op in most
     * browsers — the seek is silently dropped and playback starts at 0. This is the
     * common case when a user clicks the waveform to seek before the first play (the
     * audio src has just been assigned and is still loading). Defer the seek until
     * metadata is available.
     */
    const HAVE_METADATA = 1
    if (el.readyState < HAVE_METADATA) {
      logger.debug('Audio metadata not ready - deferring seek', { readyState: el.readyState, secs })
      const applyDeferredSeek = () => {
        el.currentTime = secs
      }
      el.addEventListener('loadedmetadata', applyDeferredSeek, { once: true })
      return
    }

    el.currentTime = secs
  }

  const pause = useCallback(() => {
    if (!el || pending) {
      return
    }

    el.pause()
    if (onPause) {
      onPause()
    }
  }, [el, onPause, pending])

  /** Update position. We debounce to avoid a lot of updates */
  const [position, setPosition] = useState(0)
  const debouncePosition = useDelayedInvoke(100)
  useEffect(() => {
    if (!el) {
      return
    }
    const handleTimeUpdate = () => debouncePosition(() => setPosition(el.currentTime))
    el.addEventListener('timeupdate', handleTimeUpdate)
    return () => {
      el.removeEventListener('timeupdate', handleTimeUpdate)
    }
  }, [debouncePosition, el, playing])

  const togglePlay = async () => {
    if (!el) {
      return
    }
    if (el.paused) {
      await play()
    } else {
      pause()
    }
  }
  /**
   * HTMLMediaElement.duration is NaN until HAVE_METADATA (readyState >= 1). Surface
   * 0 in that case so callers (waveform, progress bar) get a sensible value to
   * render. See GH #1160.
   */
  const initialDuration = el && Number.isFinite(el.duration) ? el.duration : 0
  const [duration, setDuration] = useState(initialDuration)

  /** Track media duration. */
  useEffect(() => {
    if (!el) {
      return
    }
    const readDuration = () => {
      if (Number.isFinite(el.duration)) {
        setDuration(el.duration)
      }
    }

    // Sync up-front in case metadata loaded before the effect attached (e.g. cached
    // src, or durationchange fired between render and effect). Without this the
    // listener can miss the first event and duration stays at 0 until playback
    // starts — the bug reported in GH #1160.
    readDuration()

    el.addEventListener('durationchange', readDuration)
    el.addEventListener('loadedmetadata', readDuration)

    return () => {
      el.removeEventListener('durationchange', readDuration)
      el.removeEventListener('loadedmetadata', readDuration)
    }
  }, [el])

  return {
    playing,
    play,
    seek,
    pause,
    togglePlay,
    duration,
    position,
    audioProps: {
      crossOrigin: 'anonymous',
    },
  }
}
